import { useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import { I18n, useI18n } from "../../../hooks/useTranslations";
import { isLookup, Lookup, MenuOptionFilterValue, MenuOptionValue } from "../../../types";
import { getOptionLabel, Option } from "../HeaderMenuOption";
import { ColumnFilterProps } from "./types";

// A single filter option's value is always a scalar or a Lookup - the MenuOptionFilterValue[]
// branch of MenuOptionValue only ever represents the *collection* of selected/available values,
// never one item within it. These two helpers accept the wider MenuOptionValue so callers don't
// need to cast Exclude<MenuOptionValue, Lookup>[] element types by hand.
export const normalizeFilterValue = (value: MenuOptionValue | undefined): MenuOptionFilterValue => {
  if (value === undefined || value === "" || Array.isArray(value)) {
    return null;
  }

  return value;
};

export const getFilterOptionKey = (value: MenuOptionValue | undefined): string => {
  const normalizedValue = normalizeFilterValue(value);

  if (isLookup(normalizedValue)) {
    return `lookup:${normalizedValue.id}`;
  }

  return `${typeof normalizedValue}:${normalizedValue ?? ""}`;
};

// Toggles one option in/out of a filter's selected-values list, used by every list-based filter
// (Text/Boolean, and the "Equals"/"Exact date(s)" modes of Number/Date).
export const toggleListOption = (currentValues: Exclude<MenuOptionValue, Lookup>[], option: Option): MenuOptionValue[] => {
  const key = getFilterOptionKey(option.value);

  if (option.selected) {
    return [...currentValues, normalizeFilterValue(option.value)];
  }

  return currentValues.filter((v) => getFilterOptionKey(v) !== key);
};

// selected first; then dates chronologically, numbers numerically, everything else by label
export const compareOptions = (
  a: Option,
  b: Option,
  formatter: ColumnFilterProps["formatter"],
  i18n: I18n,
): number => {
  if (!!a.selected !== !!b.selected) {
    return a.selected ? -1 : 1;
  }

  if (typeof a.value === "number" && typeof b.value === "number") {
    return a.value - b.value;
  }

  if (typeof a.value === "string" && typeof b.value === "string") {
    const dateA = new Date(a.value);
    const dateB = new Date(b.value);
    if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
      return dateA.getTime() - dateB.getTime();
    }
  }

  const labelCompare = getOptionLabel(a.value, formatter, i18n).localeCompare(
    getOptionLabel(b.value, formatter, i18n),
    i18n.language,
  );

  if (labelCompare !== 0) {
    return labelCompare;
  }

  return getFilterOptionKey(a.value).localeCompare(getFilterOptionKey(b.value), i18n.language);
};

export const filterOptionsBySearch = (
  options: Option[],
  searchTerm: string,
  formatter: ColumnFilterProps["formatter"],
  i18n: I18n,
): Option[] => {
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase(i18n.language);
  if (!normalizedSearchTerm) {
    return options;
  }

  return options.filter((option) =>
    getOptionLabel(option.value, formatter, i18n).toLocaleLowerCase(i18n.language).includes(normalizedSearchTerm),
  );
};

// Fetches the distinct values for a column from the server (on mount) and keeps them in sync with
// which ones are currently selected. Shared by every list-based filter UI (Text/Boolean, and the
// "Equals"/"Exact date(s)" modes of Number/Date).
export const useDistinctValues = (
  get: ColumnFilterProps["get"],
  fieldName: string,
  selectedValues: Exclude<MenuOptionValue, Lookup>[],
) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const result = get(fieldName, aborter.current.signal);

  const [options, setOptions] = useState<Option[]>([]);

  const selectedKey = selectedValues.map(getFilterOptionKey).sort().join("|");

  useEffect(() => {
    result.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    result.error && dispatch(showErrorNotification(i18n, result.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.error]);

  useEffect(() => {
    if (!result.data) return;

    const selectedOptionKeys = selectedValues.map(getFilterOptionKey);
    const nextOptions: Option[] = [];

    const filterValues = result.data as MenuOptionFilterValue[];
    filterValues.forEach((value) => {
      const normalizedValue = normalizeFilterValue(value);
      const key = getFilterOptionKey(normalizedValue);

      if (!nextOptions.some((option) => getFilterOptionKey(option.value) === key)) {
        nextOptions.push({ value: normalizedValue, selected: selectedOptionKeys.includes(key) });
      }
    });

    setOptions(nextOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data]);

  useEffect(() => {
    const selectedOptionKeys = selectedValues.map(getFilterOptionKey);

    setOptions((currentOptions) => {
      let hasChanges = false;

      const nextOptions = currentOptions.map((option) => {
        const selected = selectedOptionKeys.includes(getFilterOptionKey(option.value));

        if (!!option.selected === selected) {
          return option;
        }

        hasChanges = true;
        return { ...option, selected };
      });

      return hasChanges ? nextOptions : currentOptions;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  return { options, isLoading: !result.data && !result.error, error: result.error };
};
